import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { useI18n } from "../lib/i18n";

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};

/**
 * Compact month-grid calendar of bookings + selected-day list pane.
 *
 * Props:
 * - items: [{ id, start_date, end_date, service_title, status, bucket, ... }]
 * - todayISO: string "YYYY-MM-DD"
 * - onSelectDay(iso): optional — parent can react to selection
 * - statusChip(status): function returning { label, chip } for the status pill
 */
export default function ProviderBookingCalendar({ items = [], todayISO, statusChip }) {
  const { t } = useI18n();
  const today = parseISO(todayISO) || new Date();
  const [cursor, setCursor] = useState(() => new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)));
  const [selectedISO, setSelectedISO] = useState(todayISO);

  // Build a map: ISO date -> [bookings spanning that day]
  const dayMap = useMemo(() => {
    const map = new Map();
    items.forEach((b) => {
      const start = parseISO(b.start_date);
      const end = parseISO(b.end_date) || start;
      if (!start) return;
      const cur = new Date(start.getTime());
      while (cur.getTime() <= end.getTime()) {
        const k = ymd(cur);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(b);
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
    });
    return map;
  }, [items]);

  // Calendar grid cells (always 6 weeks × 7 days for a stable layout)
  const grid = useMemo(() => {
    const first = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
    // Week starts Monday — shift weekday so Monday=0…Sunday=6
    const dow = (first.getUTCDay() + 6) % 7;
    const start = new Date(first.getTime());
    start.setUTCDate(first.getUTCDate() - dow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getTime());
      d.setUTCDate(start.getUTCDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" });
  const weekLabels = [
    t("calendar.dow_mon", "Mon"),
    t("calendar.dow_tue", "Tue"),
    t("calendar.dow_wed", "Wed"),
    t("calendar.dow_thu", "Thu"),
    t("calendar.dow_fri", "Fri"),
    t("calendar.dow_sat", "Sat"),
    t("calendar.dow_sun", "Sun"),
  ];

  const move = (delta) =>
    setCursor((c) => new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + delta, 1)));

  const goToday = () => {
    const todayObj = parseISO(todayISO) || new Date();
    setCursor(new Date(Date.UTC(todayObj.getUTCFullYear(), todayObj.getUTCMonth(), 1)));
    setSelectedISO(todayISO);
  };

  const selectedBookings = selectedISO ? (dayMap.get(selectedISO) || []) : [];

  return (
    <div className="grid lg:grid-cols-[1fr,360px] gap-5" data-testid="prov-calendar">
      {/* Calendar grid */}
      <div className="luxe-card p-4 md:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <CalendarIcon size={16} className="text-teal" />
            <div className="font-serif-display text-2xl capitalize" data-testid="cal-month-label">{monthLabel}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => move(-1)} className="btn btn-ghost !px-2 !py-1.5" aria-label="Previous month" data-testid="cal-prev">
              <ChevronLeft size={14} />
            </button>
            <button onClick={goToday} className="btn btn-ghost text-[12px]" data-testid="cal-today">
              {t("calendar.today", "Today")}
            </button>
            <button onClick={() => move(1)} className="btn btn-ghost !px-2 !py-1.5" aria-label="Next month" data-testid="cal-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekLabels.map((d) => (
            <div key={d} className="label-tiny text-center">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map((d) => {
            const iso = ymd(d);
            const inMonth = d.getUTCMonth() === cursor.getUTCMonth();
            const isToday = iso === todayISO;
            const isSelected = iso === selectedISO;
            const bookings = dayMap.get(iso) || [];
            const count = bookings.length;
            const hasDispute = bookings.some((b) => b.in_dispute);
            return (
              <button
                key={iso}
                onClick={() => setSelectedISO(iso)}
                className={`relative aspect-square rounded-lg p-1.5 text-left text-xs transition-colors flex flex-col ${
                  isSelected ? "bg-teal/15 border border-teal/60"
                    : isToday ? "border border-teal/30 bg-white/[0.02]"
                    : inMonth ? "hover:bg-white/[0.04] border border-transparent"
                    : "opacity-40 border border-transparent"
                }`}
                data-testid={`cal-cell-${iso}`}
                aria-pressed={isSelected}
              >
                <div className="flex items-center justify-between">
                  <span className={`${isToday ? "text-teal font-semibold" : "text-muted"}`}>{d.getUTCDate()}</span>
                  {count > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                        hasDispute ? "bg-rose-500/30 text-rose-100" : "bg-teal/30 text-bone"
                      }`}
                      data-testid={`cal-cell-count-${iso}`}
                    >
                      {count}
                    </span>
                  )}
                </div>
                {count > 0 && (
                  <div className="mt-auto flex flex-wrap gap-0.5">
                    {bookings.slice(0, 3).map((b) => (
                      <span
                        key={b.id}
                        className={`h-1.5 w-1.5 rounded-full ${
                          b.in_dispute
                            ? "bg-rose-400"
                            : b.bucket === "past"
                              ? "bg-white/30"
                              : b.bucket === "current"
                                ? "bg-champagne"
                                : "bg-teal"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected-day pane */}
      <div className="luxe-card p-4 md:p-5" data-testid="prov-cal-day-pane">
        <div className="label-tiny mb-1">{t("calendar.selected_day", "Selected day")}</div>
        <div className="font-serif-display text-2xl">{selectedISO || "—"}</div>
        {selectedBookings.length === 0 ? (
          <div className="mt-4 text-sm text-muted">
            {t("calendar.no_bookings_on_day", "No bookings on this day.")}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {selectedBookings.map((b) => {
              const chip = statusChip ? statusChip(b.status) : { label: b.status, chip: "" };
              return (
                <div key={b.id} className="luxe-card p-3" data-testid={`cal-day-item-${b.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`chip text-[10px] ${chip.chip}`}>{chip.label}</span>
                    {b.in_dispute && <span className="chip danger text-[10px]">{t("dispute.open_chip", "In dispute")}</span>}
                    <span className="text-[11px] text-muted font-mono">#{(b.id || "").slice(0, 8)}</span>
                  </div>
                  <div className="font-serif-display text-base truncate">{b.service_title}</div>
                  <div className="text-[11px] text-muted mt-0.5">
                    {b.user_name || b.user_email} · {b.guests}{" "}
                    {t("common.guests_short", "guests")}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5 pt-4 border-t hairline">
          <div className="label-tiny mb-2">{t("calendar.legend", "Legend")}</div>
          <div className="space-y-1.5 text-xs text-muted">
            <LegendDot color="bg-teal" label={t("calendar.legend_upcoming", "Upcoming")} />
            <LegendDot color="bg-champagne" label={t("calendar.legend_current", "In progress")} />
            <LegendDot color="bg-white/30" label={t("calendar.legend_past", "Past")} />
            <LegendDot color="bg-rose-400" label={t("calendar.legend_dispute", "In dispute")} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}
